import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, isDevMode } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import type { OktaAuth } from '@okta/okta-auth-js';

import { AuthInterceptor } from '../../../shared/utils/auth/auth.interceptor';
import { APP_CONFIG, type AppConfig } from '../../../shared/utils/config/app-config';

import type { AiChatPageContext } from './ai-chat-page-context.service';

export type NavigationAction = {
    label: string;
    route: string;
    requiresPermission?: string;
    queryParams?: Record<string, string>;
};

export type ChatStreamEvent =
    | { type: 'delta'; text: string }
    | { type: 'tool_start'; name: string }
    | { type: 'tool_end'; name: string; durationMs: number }
    | { type: 'navigation'; actions: NavigationAction[] }
    | { type: 'context_warning'; utilization: number }
    | { type: 'error'; message: string }
    | { type: 'done'; contextUtilization: number; threadId: string; title?: string };

/**
 * True for same-app absolute paths (`/customers/...`). False for schemes, protocol-relative
 * URLs, fragments-only, and empty values.
 */
export function isInAppNavigationUrl(url: string): boolean {
    const trimmed = url.trim();
    if (trimmed.length === 0 || trimmed.startsWith('#')) {
        return false;
    }
    if (trimmed.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
        return false;
    }
    return trimmed.startsWith('/');
}

@Injectable({ providedIn: 'root' })
export class AiChatService {
    private readonly router = inject(Router);
    private readonly http = inject(HttpClient);
    private readonly appConfig: AppConfig = inject(APP_CONFIG);
    private readonly oktaAuth: OktaAuth = inject(OKTA_AUTH);

    async streamMessage(
        threadId: string,
        content: string,
        pageContext: AiChatPageContext,
        onEvent: (event: ChatStreamEvent) => void
    ): Promise<void> {
        if (this.hasDirectHttpSseUrl()) {
            await this.streamMessageViaFetch(threadId, content, pageContext, onEvent);
            return;
        }
        await this.streamMessageViaHttpClient(threadId, content, pageContext, onEvent);
    }

    /**
     * Navigate within the SPA using an absolute path from the route registry.
     * Uses `navigateByUrl` so multi-segment paths like `/customers/:id` are not treated as
     * a single encoded URL segment (which breaks `router.navigate([path])`).
     */
    navigate(action: NavigationAction): void {
        this.navigateInAppUrl(action.route, action.queryParams);
    }

    /**
     * SPA-navigate to an in-app URL. Returns true when handled; false when the URL should
     * use the browser default (external links, mailto, etc.).
     */
    navigateInAppUrl(url: string, queryParams?: Record<string, string>): boolean {
        if (!isInAppNavigationUrl(url)) {
            return false;
        }
        const tree = this.router.parseUrl(url);
        if (queryParams != null) {
            tree.queryParams = { ...tree.queryParams, ...queryParams };
        }
        void this.router.navigateByUrl(tree);
        return true;
    }

    /**
     * Direct `http://` SSE URL for local prototyping (bypasses the Angular dev proxy). Relative
     * `/api/assistant/chat` uses HttpClient so the dev proxy can keep the long-lived connection open.
     */
    private hasDirectHttpSseUrl(): boolean {
        const override = this.appConfig.aiChatSseUrl?.trim();
        return override != null && override.length > 0;
    }

    private isLocalPrototyping(): boolean {
        return isDevMode() || this.appConfig.environmentName === 'acm-local';
    }

    private resolveChatUrl(): string {
        const override = this.appConfig.aiChatSseUrl?.trim();
        if (override != null && override.length > 0) {
            if (!this.isLocalPrototyping()) {
                throw new Error('aiChatSseUrl is only supported for local development');
            }
            let parsed: URL;
            try {
                parsed = new URL(override);
            } catch {
                throw new Error('aiChatSseUrl must be a valid absolute URL');
            }
            if (parsed.protocol !== 'http:') {
                throw new Error('aiChatSseUrl must use http:// for local SSE prototyping');
            }
            return override;
        }
        return '/api/assistant/chat';
    }

    private buildChatRequestHeaders(requestUrl: string): Record<string, string> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
        };
        const allowedOrigins = this.appConfig.authenticatedEndpoints;
        if (allowedOrigins.some((fragment) => requestUrl.includes(fragment))) {
            const accessToken = this.oktaAuth.getAccessToken();
            if (accessToken) {
                headers[AuthInterceptor.AUTH_HEADER_NAME] = `Bearer ${accessToken}`;
            }
        }
        return headers;
    }

    private async streamMessageViaFetch(
        threadId: string,
        content: string,
        pageContext: AiChatPageContext,
        onEvent: (event: ChatStreamEvent) => void
    ): Promise<void> {
        const url = this.resolveChatUrl();
        const response = await fetch(url, {
            method: 'POST',
            headers: this.buildChatRequestHeaders(url),
            body: JSON.stringify({
                threadId,
                message: { role: 'user', content },
                pageContext,
            }),
        });

        if (!response.ok) {
            throw new Error(`Chat request failed (${response.status})`);
        }

        const reader = response.body?.getReader();
        if (reader == null) {
            throw new Error('Chat request failed: no response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            buffer = this.drainCompleteSseChunks(buffer, onEvent);
        }
        buffer += decoder.decode();
        this.flushTrailingSseBuffer(buffer, onEvent);
    }

    private async streamMessageViaHttpClient(
        threadId: string,
        content: string,
        pageContext: AiChatPageContext,
        onEvent: (event: ChatStreamEvent) => void
    ): Promise<void> {
        await new Promise<void>((resolve, reject) => {
            let buffer = '';
            let processedLength = 0;

            const appendAndParse = (cumulativeText: string): void => {
                if (cumulativeText.length <= processedLength) {
                    return;
                }
                buffer += cumulativeText.slice(processedLength);
                processedLength = cumulativeText.length;
                buffer = this.drainCompleteSseChunks(buffer, onEvent);
            };

            const flushTrailingBuffer = (): void => {
                this.flushTrailingSseBuffer(buffer, onEvent);
                buffer = '';
            };

            this.http
                .post(
                    '/api/assistant/chat',
                    {
                        threadId,
                        message: { role: 'user', content },
                        pageContext,
                    },
                    {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                            Accept: 'text/event-stream',
                        }),
                        observe: 'events',
                        reportProgress: true,
                        responseType: 'text',
                    }
                )
                .subscribe({
                    next: (event) => {
                        if (event.type === HttpEventType.DownloadProgress) {
                            if (event.partialText != null) {
                                appendAndParse(event.partialText);
                            }
                        } else if (event.type === HttpEventType.Response) {
                            if (typeof event.body === 'string') {
                                appendAndParse(event.body);
                            }
                            flushTrailingBuffer();
                            resolve();
                        }
                    },
                    error: (err: unknown) => {
                        const message =
                            err instanceof HttpErrorResponse
                                ? `Chat request failed (${err.status})`
                                : 'Chat request failed';
                        reject(new Error(message));
                    },
                });
        });
    }

    private drainCompleteSseChunks(
        buffer: string,
        onEvent: (event: ChatStreamEvent) => void
    ): string {
        const chunks = buffer.split('\n\n');
        const remainder = chunks.pop() ?? '';
        for (const chunk of chunks) {
            const event = this.parseSseChunk(chunk);
            if (event != null) {
                onEvent(event);
            }
        }
        return remainder;
    }

    private flushTrailingSseBuffer(
        buffer: string,
        onEvent: (event: ChatStreamEvent) => void
    ): void {
        if (buffer.trim().length === 0) {
            return;
        }
        const event = this.parseSseChunk(buffer);
        if (event != null) {
            onEvent(event);
        }
    }

    private parseSseChunk(chunk: string): ChatStreamEvent | null {
        const lines = chunk.split('\n');
        let eventType: string | null = null;
        let dataLine: string | null = null;
        for (const line of lines) {
            if (line.startsWith('event: ')) {
                eventType = line.slice('event: '.length).trim();
            } else if (line.startsWith('data: ')) {
                dataLine = line.slice('data: '.length);
            }
        }
        if (eventType == null || dataLine == null) {
            return null;
        }
        try {
            const payload = JSON.parse(dataLine) as Record<string, unknown>;
            return { ...payload, type: eventType } as ChatStreamEvent;
        } catch {
            return null;
        }
    }
}
